import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, FileSpreadsheet, Loader as Loader2 } from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { BankAccount, AccountingCategory } from '../../types/database';
import { bankTransactionsService, CreateBankTransactionData } from '../../lib/bankTransactionsService';
import { bankCategoryRulesService } from '../../lib/bankCategoryRulesService';
import { dispatchNotification } from '../../lib/notificationPreferencesService';
import { supabase } from '../../lib/supabase';

// ─── Sanitization helpers (do NOT alter validation logic below) ───────────────

/**
 * sanitizeHeaders
 * Trims leading/trailing whitespace from every header name so that the
 * existing validateHeaders() check (which does an exact-string comparison)
 * succeeds even when the spreadsheet contains " סכום " instead of "סכום".
 */
function sanitizeHeaders(rawHeaders: string[]): string[] {
  return rawHeaders.map(h => h.trim());
}

/**
 * sanitizeDate
 * Converts any of the three supported raw date representations into a clean
 * string that the existing parseDate() function can handle (DD/MM/YYYY).
 *
 * Supported input formats:
 *   1. DD/MM/YYYY  – already correct, returned unchanged
 *   2. YYYY-MM-DD  – ISO / PostgreSQL; converted to DD/MM/YYYY
 *   3. Excel serial number (numeric) – decoded via XLSX.SSF.parse_date_code
 *
 * The returned value is ALWAYS in DD/MM/YYYY so parseDate() stays untouched.
 */
function sanitizeDate(raw: unknown): string {
  // --- Excel serial number (number type) ---
  if (typeof raw === 'number') {
    // Valid Excel serial range: 1 (1900-01-01) … 2958465 (9999-12-31)
    if (raw >= 1 && raw <= 2958465) {
      const parsed = XLSX.SSF.parse_date_code(raw);
      if (parsed && parsed.y && parsed.m && parsed.d) {
        const dd = String(parsed.d).padStart(2, '0');
        const mm = String(parsed.m).padStart(2, '0');
        return `${dd}/${mm}/${parsed.y}`;
      }
    }
    // Fall through and let parseDate() reject it
    return String(raw);
  }

  const s = String(raw ?? '').trim();

  // --- ISO / PostgreSQL format YYYY-MM-DD ---
  // Matches exactly "YYYY-MM-DD" (with optional T-time suffix)
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    // Convert to DD/MM/YYYY so the existing parseDate() logic works
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  // --- Already DD/MM/YYYY (or any other string) — return as-is ---
  return s;
}

/**
 * sanitizeAmount
 * Removes thousands-separator commas from amount strings before the value
 * reaches parseAmount().  All other cleaning (currency symbols, spaces) is
 * already handled inside parseAmount() and is NOT duplicated here.
 *
 * Examples:  "58,447.00"  → "58447.00"
 *            "-1,200.50" → "-1200.50"
 *            1234        → 1234   (numbers passed through unchanged)
 */
function sanitizeAmount(raw: unknown): string | number {
  if (typeof raw === 'number') return raw;
  // Remove only digit-grouping commas (e.g. "58,447.00" → "58447.00")
  // A comma is a thousands separator when it sits between digits.
  return String(raw ?? '').replace(/(\d),(\d)/g, '$1$2');
}

interface ImportTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (importedIds?: string[]) => void;
  bankAccounts: BankAccount[];
  /** @deprecated No longer used — categories are applied only via auto-rules or left NULL */
  accountingCategories?: AccountingCategory[];
  companyId: string;
}

interface ParsedRow {
  תאריך: string;
  תיאור: string;
  סכום: number | string;
}

export function ImportTransactionsModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  bankAccounts,
  // accountingCategories is kept in props for backward-compat but no longer used
  // (categories are applied only via auto-categorisation rules or left NULL)
  companyId 
}: ImportTransactionsModalProps) {
  const { t } = useTranslation();
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  if (!isOpen) return null;

  const resetForm = () => {
    setSelectedAccountId('');
    setFile(null);
    setError(null);
    setValidationErrors([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateFile(droppedFile);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateFile(selectedFile);
    }
  };

  const validateFile = (file: File) => {
    const extension = file.name.toLowerCase().split('.').pop();
    
    if (extension !== 'csv' && extension !== 'xlsx') {
      setError(t('cashflow.import.invalidFileType'));
      setFile(null);
      return;
    }

    setFile(file);
    setError(null);
    setValidationErrors([]);
  };

  const parseDate = (dateStr: string): string | null => {
    // Support DD/MM/YYYY format
    const parts = dateStr.trim().split('/');
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0] || '', 10);
    const month = parseInt(parts[1] || '', 10);
    const year = parseInt(parts[2] || '', 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    if (day < 1 || day > 31 || month < 1 || month > 12) return null;

    // Return PostgreSQL date format YYYY-MM-DD
    const paddedMonth = month.toString().padStart(2, '0');
    const paddedDay = day.toString().padStart(2, '0');
    return `${year}-${paddedMonth}-${paddedDay}`;
    // ↑ parseDate is intentionally unchanged — sanitizeDate() normalises
    //   inputs to DD/MM/YYYY before this function is ever called.
  };

  const parseAmount = (amountStr: string | number): number | null => {
    if (typeof amountStr === 'number') return amountStr;
    
    // Remove currency symbols, commas, spaces
    const cleaned = amountStr.toString().replace(/[₪$€,\s]/g, '');
    const amount = parseFloat(cleaned);
    
    return isNaN(amount) ? null : amount;
  };

  const validateHeaders = (headers: string[]): boolean => {
    const expected = ['תאריך', 'תיאור', 'סכום'];
    
    if (headers.length < 3) return false;
    
    // Check first 3 columns match exactly
    for (let i = 0; i < 3; i++) {
      if (headers[i]?.trim() !== expected[i]) {
        return false;
      }
    }
    
    return true;
  };

  const processCSV = (file: File): Promise<ParsedRow[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim(), // ← sanitize header whitespace
        complete: (results) => {
          // Check headers — validation logic unchanged
          const headers = sanitizeHeaders(results.meta.fields || []);
          if (!validateHeaders(headers)) {
            reject(new Error(t('cashflow.import.invalidHeaders')));
            return;
          }

          // Sanitize each raw row's date and amount before passing to
          // the existing parseDate / parseAmount validation pipeline.
          const sanitized = (results.data as Record<string, unknown>[]).map(row => ({
            תאריך: sanitizeDate(row['תאריך']),
            תיאור: String(row['תיאור'] ?? ''),
            סכום:  sanitizeAmount(row['סכום']),
          } as ParsedRow));

          resolve(sanitized);
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  };

  const processXLSX = (file: File): Promise<ParsedRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get first sheet
          const firstSheetName = workbook.SheetNames[0];
          if (!firstSheetName) {
            reject(new Error(t('cashflow.import.emptyFile')));
            return;
          }
          
          const worksheet = workbook.Sheets[firstSheetName];
          if (!worksheet) {
            reject(new Error(t('cashflow.import.emptyFile')));
            return;
          }
          
          // Convert to JSON with header row
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length < 2) {
            reject(new Error(t('cashflow.import.emptyFile')));
            return;
          }

          // Check headers (first row) — sanitize whitespace then validate (unchanged logic)
          const headers = sanitizeHeaders(
            (jsonData[0] as unknown[]).map(h => (h != null ? String(h) : ''))
          );
          if (!validateHeaders(headers)) {
            reject(new Error(t('cashflow.import.invalidHeaders')));
            return;
          }

          // Convert rows to objects, sanitizing date and amount values so the
          // existing parseDate / parseAmount validators receive clean inputs.
          const rows: ParsedRow[] = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i] as unknown[];
            if (row && row.length >= 3) {
              rows.push({
                // sanitizeDate handles Excel serials, ISO strings, and DD/MM/YYYY
                תאריך: sanitizeDate(row[0]),
                תיאור: row[1] != null ? String(row[1]).trim() : '',
                // sanitizeAmount strips thousands-separator commas
                סכום:  sanitizeAmount(row[2]),
              });
            }
          }

          resolve(rows);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error(t('cashflow.import.fileReadError')));
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const handleImport = async () => {
    if (!file || !selectedAccountId) {
      setError(t('cashflow.import.selectAccountAndFile'));
      return;
    }

    setUploading(true);
    setError(null);
    setValidationErrors([]);

    try {
      // Parse file based on extension
      const extension = file.name.toLowerCase().split('.').pop();
      let parsedRows: ParsedRow[];

      if (extension === 'csv') {
        parsedRows = await processCSV(file);
      } else if (extension === 'xlsx') {
        parsedRows = await processXLSX(file);
      } else {
        throw new Error(t('cashflow.import.invalidFileType'));
      }

      // Validate and convert rows
      const transactions: CreateBankTransactionData[] = [];
      const errors: string[] = [];

      // ── Build auto-categorization rules list once per import ──────────
      // Each rule is checked as a "contains" match against the description.
      // Rows that match a rule get auto-classified; all others stay NULL.
      const categoryRules = await bankCategoryRulesService.buildRulesList(companyId);

      parsedRows.forEach((row, index) => {
        const rowNum = index + 2; // Account for header row + 0-based index

        // Validate date
        const date = parseDate(row.תאריך);
        if (!date) {
          errors.push(t('cashflow.import.invalidDateRow', { row: rowNum }));
          return;
        }

        // Validate description
        const description = row.תיאור?.toString().trim();
        if (!description) {
          errors.push(t('cashflow.import.invalidDescriptionRow', { row: rowNum }));
          return;
        }

        // Validate amount
        const amount = parseAmount(row.סכום);
        if (amount === null) {
          errors.push(t('cashflow.import.invalidAmountRow', { row: rowNum }));
          return;
        }

        const autoCategory = bankCategoryRulesService.matchCategory(description, categoryRules);

        // Create transaction data
        transactions.push({
          company_id: companyId,
          bank_account_id: selectedAccountId,
          transaction_date: date,
          description: description,
          accounting_category_id: autoCategory,
          amount: amount
        });
      });

      // Show validation errors if any
      if (errors.length > 0) {
        setValidationErrors(errors.slice(0, 10)); // Show first 10 errors
        if (transactions.length === 0) {
          setUploading(false);
          return;
        }
      }

      // Bulk insert transactions
      if (transactions.length > 0) {
        const result = await bankTransactionsService.bulkCreateTransactions(transactions);
        
        if (result.error) {
          const errorMsg = typeof result.error === 'object' && result.error && 'message' in result.error 
            ? String(result.error.message) 
            : String(result.error);
          throw new Error(errorMsg);
        }

        const importedIds = (result.data ?? []).map((t) => t.id);

        // Notify company admins about new unmatched transactions
        if (companyId) {
          const { data: members } = await supabase
            .from('company_members')
            .select('user_id')
            .eq('company_id', companyId);
          if (members?.length) {
            dispatchNotification({
              event_key:  'cashflow.unmatched_transaction',
              company_id: companyId,
              user_ids:   members.map((m: { user_id: string }) => m.user_id),
              title:      `יובאו ${transactions.length} עסקאות חדשות`,
              message:    `יובאו ${transactions.length} עסקאות בנקאיות הממתינות להתאמה`,
              action_url: '/app/cashflow',
              payload:    { count: transactions.length },
            });
          }
        }

        handleClose();
        onSuccess(importedIds);
      }
    } catch (error: unknown) {
      console.error('Import error:', error);
      const errorMessage = error instanceof Error ? error.message : t('cashflow.import.error');
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {t('cashflow.import.title')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={uploading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Bank Account Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('cashflow.import.targetAccount')} <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={uploading}
            >
              <option value="">{t('cashflow.import.selectAccount')}</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.nickname} - {account.bank_name} ({account.account_number})
                </option>
              ))}
            </select>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              {t('cashflow.import.formatInstructions')}
            </h3>
            <div className="text-sm text-blue-800 space-y-2">
              <p>{t('cashflow.import.instructionsIntro')}</p>
              <ol className="list-decimal list-inside space-y-1 mr-4">
                <li>{t('cashflow.import.instruction1')}</li>
                <li>{t('cashflow.import.instruction2')}</li>
                <li>{t('cashflow.import.instruction3')}</li>
              </ol>
              <p className="font-medium mt-3">{t('cashflow.import.autoClassification')}</p>
            </div>
          </div>

          {/* File Upload Dropzone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragging
                ? 'border-blue-500 bg-blue-50'
                : file
                ? 'border-green-500 bg-green-50'
                : 'border-gray-300 bg-gray-50'
            }`}
          >
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
              disabled={uploading}
            />
            
            {file ? (
              <div className="space-y-3">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-600" />
                <div>
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="text-sm text-blue-600 hover:text-blue-700 underline"
                  disabled={uploading}
                >
                  {t('cashflow.import.changeFile')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <Upload className="w-12 h-12 mx-auto text-gray-400" />
                <div>
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {t('cashflow.import.clickToUpload')}
                  </label>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('cashflow.import.orDragDrop')}
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  {t('cashflow.import.supportedFormats')}
                </p>
              </div>
            )}
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-yellow-900 mb-2">
                    {t('cashflow.import.validationErrors')}
                  </h4>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                  {validationErrors.length >= 10 && (
                    <p className="text-xs text-yellow-700 mt-2">
                      {t('cashflow.import.moreErrors')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* General Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
            disabled={uploading}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleImport}
            disabled={!file || !selectedAccountId || uploading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('cashflow.import.importing')}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                {t('cashflow.import.startImport')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
